/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import React from 'react'
import Text from '../../ts-components/Text'

/**
 * @interface EntityDisplayProps
 * @property {any} entity - The entity object to display (organization, team, or schedule)
 * @property {string} entityType - The type of entity ('organization', 'team', 'schedule')
 */
interface EntityDisplayProps {
  entity: any
  entityType: string
}

/**
 * Entity Display component - displays entity information in a non-clickable format
 *
 * This component shows the entity name and image when a specific entity is targeted,
 * replacing the TargetAudienceSelector with a static display that matches the same UI style.
 * It also fetches and displays the target audience count for the entity.
 *
 * Key features:
 * - Displays entity thumbnail and full name
 * - Fetches and displays target audience count
 * - Non-clickable, read-only display
 * - Matches the visual style of TargetAudienceSelector
 * - Handles different entity types with appropriate placeholder images
 * - Shows loading state while fetching audience data
 *
 * @param props - Component props as defined in EntityDisplayProps
 * @returns JSX element representing a static entity display with audience count
 */
const EntityDisplay: React.FC<EntityDisplayProps> = React.memo(
  ({ entity, entityType }) => {
    const getPlaceholderImage = () => {
      switch (entityType) {
        case 'organization':
          return '/images/generic_placeholders/placeholder_organization.png'
        case 'team':
          return '/images/generic_placeholders/placeholder_team.png'
        case 'schedule':
          return '/images/generic_placeholders/placeholder_competition.png'
        default:
          return '/images/generic_placeholders/placeholder_organization.png'
      }
    }

    return (
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
        `}
      >
        <div
          css={css`
            padding: 8px 8px;
            gap: 4px;
            background-color: ${colors.SECONDARY_400};
            border-radius: 8px;
            color: ${colors.NEUTRAL_0};
            display: inline-flex;
            align-items: center;
            cursor: default;
          `}
        >
          <img
            src={getImageThumbnailId(entity) || getPlaceholderImage()}
            alt={entity?.name || entity?.name_full}
            width={16}
            height={16}
            css={css`
              border-radius: 4px;
              border: 2px solid ${colors.WHITE};
              background: white;
              object-fit: contain;
            `}
          />
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_0}
            style={`                
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;`}
          >
            {entity?.name || entity?.name_full}
          </Text>
        </div>
        {/* {targetAudience !== null && (
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_100}
          >
            Audience: {targetAudience}
          </Text>
        )} */}
      </div>
    )
  }
)

export default EntityDisplay
